import {
  EXTENSION_URL,
  PROCEDURES_PREOPERATIVE_DIAGNOSIS,
  PROCEDURES_POST_OPERATIVE_DIAGNOSIS,
  PROCEDURES_ELB,
  PROCEDURES_PLAN,
  EXTENSIONS_SELF_REPORTED,
} from '@constants/extension-codes';

import type { SurgeryValues } from '@models/procedure';

import { formatPatientId } from '@utils/format';

import type {
  Extension,
  LocationModelInput,
  ProcedureModelInput,
} from 'generated/types';
import { ProcedureStatus } from 'generated/types';

export function getProcedureTemplate(
  patient_id: string,
  values: SurgeryValues
): { procedure: ProcedureModelInput; location: LocationModelInput } {
  const extensions: Extension[] = [];
  if (values.preoperativeDiagnosis) {
    extensions.push({
      url: `${EXTENSION_URL}${PROCEDURES_PREOPERATIVE_DIAGNOSIS}`,
      valueString: values.preoperativeDiagnosis,
    });
  }
  if (values.postOperativeDiagnosis) {
    extensions.push({
      url: `${EXTENSION_URL}${PROCEDURES_POST_OPERATIVE_DIAGNOSIS}`,
      valueString: values.postOperativeDiagnosis,
    });
  }
  if (values.elb) {
    extensions.push({
      url: `${EXTENSION_URL}${PROCEDURES_ELB}`,
      valueString: values.elb,
    });
  }
  if (values.plan) {
    extensions.push({
      url: `${EXTENSION_URL}${PROCEDURES_PLAN}`,
      valueString: values.plan,
    });
  }
  if (values.selfReported) {
    extensions.push({
      url: `${EXTENSION_URL}${EXTENSIONS_SELF_REPORTED}`,
      valueBoolean: values.selfReported,
    });
  }
  if (values.associatedResources) {
    values.associatedResources.forEach((resource) => {
      extensions.push({
        url: 'https://vitall.com/documentation/extensions/associated-records',
        valueReference: {
          reference: resource,
        },
      });
    });
  }

  const performers = [];
  if (values.practitioner) {
    performers.push({
      actor: {
        display: values.practitioner,
        type: 'practitioner',
      },
    });
  }
  if (values.anesthesiologist) {
    performers.push({
      actor: {
        display: values.anesthesiologist,
        type: 'anesthesiologist',
      },
    });
  }
  if (values.doctor) {
    performers.push({
      actor: {
        display: values.doctor,
        type: 'doctor',
      },
    });
  }

  return {
    procedure: {
      resourceType: 'Procedure',
      subject: {
        reference: formatPatientId(patient_id),
      },
      status: ProcedureStatus.unknown,
      code: {
        text: values.title,
      },
      ...(values.procedureDate && {
        performedDateTime: values.procedureDate,
      }),
      ...(performers.length > 0 && {
        performer: performers,
      }),
      ...(values.visitReason && {
        reasonCode: [
          {
            text: values.visitReason,
          },
        ],
      }),
      ...(values.complications && {
        complication: [
          {
            text: values.complications,
          },
        ],
      }),
      ...(values.followUp && {
        followUp: [
          {
            text: values.followUp,
          },
        ],
      }),
      ...(values.procedureNotes && {
        note: [
          {
            text: values.procedureNotes,
          },
        ],
      }),
      ...(values.anesthesia && {
        usedCode: [
          {
            text: values.anesthesia,
          },
        ],
      }),
      ...(extensions.length > 0 && {
        extension: extensions,
      }),
    },

    location: {
      ...(values.facility && {
        name: values.facility,
      }),
      ...((values.city || values.country) && {
        address: {
          ...(values.city && {
            city: values.city,
          }),
          ...(values.country && {
            country: values.country,
          }),
        },
      }),
    },
  };
}
